// Color utilities for generating distinct, colorblind-friendly palettes

// Okabe–Ito colorblind-safe palette (8 colors) + extended neutrals
export const okabeItoPalette = [
  '#E69F00', // orange
  '#56B4E9', // sky blue
  '#009E73', // bluish green
  '#F0E442', // yellow
  '#0072B2', // blue
  '#D55E00', // vermillion
  '#CC79A7', // reddish purple
  '#000000', // black
] as const

export const chartBasicColors = okabeItoPalette as readonly string[]

type RGB = { r: number; g: number; b: number }

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

const hexToRgb = (hex: string): RGB => {
  const v = hex.replace('#', '')
  const bigint = parseInt(v.length === 3 ? v.replace(/(.)/g, '$1$1') : v, 16)
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 }
}

const rgbToXyz = ({ r, g, b }: RGB) => {
  // sRGB to XYZ
  const sr = r / 255
  const sg = g / 255
  const sb = b / 255
  const lin = (u: number) => (u <= 0.04045 ? u / 12.92 : Math.pow((u + 0.055) / 1.055, 2.4))
  const R = lin(sr)
  const G = lin(sg)
  const B = lin(sb)
  return {
    x: R * 0.4124 + G * 0.3576 + B * 0.1805,
    y: R * 0.2126 + G * 0.7152 + B * 0.0722,
    z: R * 0.0193 + G * 0.1192 + B * 0.9505,
  }
}

const xyzToLab = ({ x, y, z }: { x: number; y: number; z: number }) => {
  // D65 reference white
  const xr = x / 0.95047
  const yr = y / 1.0
  const zr = z / 1.08883
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : (841 / 108) * t + 4 / 29)
  const fx = f(xr)
  const fy = f(yr)
  const fz = f(zr)
  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) }
}

const hexToLab = (hex: string) => xyzToLab(rgbToXyz(hexToRgb(hex)))

const deltaE76 = (c1: string, c2: string) => {
  const a = hexToLab(c1)
  const b = hexToLab(c2)
  const dL = a.L - b.L
  const da = a.a - b.a
  const db = a.b - b.b
  return Math.sqrt(dL * dL + da * da + db * db)
}

const hslToRgb = (h: number, s: number, l: number): RGB => {
  const C = (1 - Math.abs(2 * l - 1)) * s
  const Hp = h / 60
  const X = C * (1 - Math.abs((Hp % 2) - 1))
  let r = 0,
    g = 0,
    b = 0
  if (0 <= Hp && Hp < 1) [r, g, b] = [C, X, 0]
  else if (1 <= Hp && Hp < 2) [r, g, b] = [X, C, 0]
  else if (2 <= Hp && Hp < 3) [r, g, b] = [0, C, X]
  else if (3 <= Hp && Hp < 4) [r, g, b] = [0, X, C]
  else if (4 <= Hp && Hp < 5) [r, g, b] = [X, 0, C]
  else if (5 <= Hp && Hp < 6) [r, g, b] = [C, 0, X]
  const m = l - C / 2
  return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) }
}

const rgbToHex = ({ r, g, b }: RGB): string =>
  `#${[r, g, b]
    .map((v) => {
      const s = Math.round(clamp01(v / 255) * 255).toString(16).toUpperCase()
      return s.length === 1 ? `0${s}` : s
    })
    .join('')}`

const hslToHex = (h: number, s: number, l: number) => rgbToHex(hslToRgb(h, s, l))

const generateHslCandidates = (count: number, startHue = 0): string[] => {
  const colors: string[] = []
  const goldenAngle = 137.508 // distribute hues uniformly
  let hue = startHue
  for (let i = 0; i < count; i++) {
    const h = (hue + i * goldenAngle) % 360
    const s = 0.65
    const l = 0.55
    colors.push(hslToHex(h, s, l))
  }
  return colors
}

export const generateDistinctPalette = (n: number): string[] => {
  if (n <= okabeItoPalette.length) return okabeItoPalette.slice(0, n)

  // Start with Okabe–Ito, extend with HSL candidates
  const needed = n - okabeItoPalette.length
  const candidates = generateHslCandidates(Math.max(needed * 4, 24))
  const selected: string[] = [...okabeItoPalette]

  // Greedy farthest-point sampling in Lab space
  const minDistanceTarget = 22 // reasonable perceptual threshold
  while (selected.length < n && candidates.length > 0) {
    let bestIdx = 0
    let bestScore = -1
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i]
      let minD = Infinity
      for (const s of selected) {
        const d = deltaE76(c, s)
        if (d < minD) minD = d
        if (minD < bestScore) break
      }
      if (minD > bestScore) {
        bestScore = minD
        bestIdx = i
      }
    }
    const chosen = candidates.splice(bestIdx, 1)[0]
    // If too close, tweak hue slightly to increase distance
    if (bestScore < minDistanceTarget) {
      // try offset hues around chosen
      const tweaks = [15, -15, 30, -30, 45, -45]
      let improved = chosen
      let improvedScore = bestScore
      for (const t of tweaks) {
        const { r, g, b } = hexToRgb(chosen)
        // derive hue from RGB roughly is messy; instead regenerate around random hue
        // We'll sample a few alternatives via new hues seeded by index
        const alt = hslToHex((t + selected.length * 23) % 360, 0.65, 0.55)
        let minDAlt = Infinity
        for (const s of selected) {
          const d = deltaE76(alt, s)
          if (d < minDAlt) minDAlt = d
        }
        if (minDAlt > improvedScore) {
          improvedScore = minDAlt
          improved = alt
        }
      }
      selected.push(improved)
    } else {
      selected.push(chosen)
    }
  }
  return selected.slice(0, n)
}

export const getColorMapForIds = (ids: string[]): { [id: string]: string } => {
  const unique = Array.from(new Set(ids)).sort() // stable across refresh
  const palette = generateDistinctPalette(unique.length)
  const map: { [id: string]: string } = {}
  unique.forEach((id, idx) => (map[id] = palette[idx]))
  return map
}

export const colorForIdStable = (id: string): string => {
  // Simple string hash (djb2)
  let hash = 5381
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 33) ^ id.charCodeAt(i)
  }
  const idx = Math.abs(hash) % okabeItoPalette.length
  return okabeItoPalette[idx]
}
